import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import PublicNavbar from "../components/PublicNavbar";
import { usePageContent, renderHTML } from "../hooks/usePageContent";
import {
  Database,
  ArrowLeft,
  Server,
  Code2,
  Zap,
  Shield,
  BarChart3,
  History,
  Bookmark,
  FolderTree,
  FileDown,
  Bot,
  Check,
  CreditCard,
  Users,
  Globe
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AboutPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const { getContent } = usePageContent('about');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        setSettings(response.data);
        if (response.data.logo_url) setLogoUrl(response.data.logo_url);
      } catch (error) {
        console.error("Failed to fetch settings");
      }
    };
    fetchSettings();
  }, []);

  const siteName = settings?.site_name || "AIQueryStudio";
  const tagline = settings?.tagline || "Professional Database Management";
  const companyName = settings?.company_name || "AIQueryStudio Inc.";

  // SEO-optimized summary
  const seoDescription = `${siteName} is a comprehensive web-based database management tool supporting 12+ database types including MS SQL Server, MySQL, PostgreSQL, and MongoDB. Features include AI-powered query generation, visual database explorer, query history, saved queries, real-time metrics, and credit-based pricing. Perfect for developers, database administrators, and teams seeking professional database management solutions without installation.`;

  const features = [
    {
      icon: Database,
      title: "Multi-Database Support",
      description: "Connect to 12+ database types including MS SQL Server, MySQL, PostgreSQL, MongoDB, Oracle, MariaDB, Redis, Cassandra, DynamoDB, Couchbase, Firebase, and SQLite. Seamlessly manage all your databases from one platform.",
      color: "text-blue-500"
    },
    {
      icon: Code2,
      title: "Advanced Query Editor",
      description: "Multi-tab query editor with syntax highlighting, auto-completion, and query validation. Execute complex queries with ease and view results in customizable table formats with sorting and filtering capabilities.",
      color: "text-green-500"
    },
    {
      icon: Bot,
      title: "AI-Powered Query Generation",
      description: "Transform natural language into SQL queries using AI. Describe what you want in plain English, and our AI will generate the corresponding SQL query. Also features AI-to-SQL and SQL-to-AI translation for better understanding.",
      color: "text-purple-500"
    },
    {
      icon: FolderTree,
      title: "Visual Database Explorer",
      description: "Browse database schemas visually with an intuitive tree structure. View tables, collections, columns, indexes, and relationships. Perform CRUD operations directly from the explorer with real-time updates.",
      color: "text-amber-500"
    },
    {
      icon: History,
      title: "Complete Query History",
      description: "Track all executed queries with timestamps, execution times, and results. Filter by connection, date range, or success status. Re-run previous queries with a single click and analyze query performance over time.",
      color: "text-cyan-500"
    },
    {
      icon: Bookmark,
      title: "Saved Queries Library",
      description: "Save frequently used queries for quick access. Organize queries by connection, add descriptions, and include the original AI prompts. Create a personal library of reusable query templates.",
      color: "text-pink-500"
    },
    {
      icon: BarChart3,
      title: "Real-Time Metrics Dashboard",
      description: "Monitor database performance with comprehensive analytics. Track query execution times, success rates, connection usage, and credit consumption. Identify slow queries and optimize performance.",
      color: "text-orange-500"
    },
    {
      icon: FileDown,
      title: "Data Export & Import",
      description: "Export query results to CSV, JSON, or Excel formats. Import data from files directly into your databases. Bulk operations supported for efficient data management and migration.",
      color: "text-indigo-500"
    },
    {
      icon: Shield,
      title: "Enterprise-Grade Security",
      description: "All database credentials are encrypted at rest and in transit. SSL/TLS connections, JWT authentication, Google OAuth support, and role-based access control ensure your data remains secure.",
      color: "text-red-500"
    },
    {
      icon: CreditCard,
      title: "Flexible Credit System",
      description: "Pay only for what you use with our credit-based pricing. No subscriptions, no long-term commitments. Purchase credits in packages that suit your needs. Credits never expire.",
      color: "text-emerald-500"
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Share database connections and queries with team members. Collaborate on query development and maintain consistency across your organization. Admin dashboard for user management.",
      color: "text-violet-500"
    },
    {
      icon: Globe,
      title: "Web-Based Access",
      description: "No installation required. Access your databases from any browser, any device, anywhere. Progressive Web App (PWA) support for offline access and native-like experience.",
      color: "text-teal-500"
    }
  ];

  const databaseTypes = [
    { name: "MS SQL Server", icon: "🗄️" },
    { name: "MySQL", icon: "🐬" },
    { name: "PostgreSQL", icon: "🐘" },
    { name: "MongoDB", icon: "🍃" },
    { name: "Oracle", icon: "🔴" },
    { name: "MariaDB", icon: "🦭" },
    { name: "Redis", icon: "⚡" },
    { name: "Cassandra", icon: "💎" },
    { name: "DynamoDB", icon: "⚙️" },
    { name: "Couchbase", icon: "🛋️" },
    { name: "Firebase", icon: "🔥" },
    { name: "SQLite", icon: "📦" }
  ];

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* SEO Meta Description (hidden, for crawlers) */}
      <div className="hidden" itemScope itemType="https://schema.org/WebApplication">
        <meta itemProp="name" content={siteName} />
        <meta itemProp="description" content={seoDescription} />
        <meta itemProp="applicationCategory" content="Database Management Software" />
        <meta itemProp="operatingSystem" content="Web Browser" />
      </div>

      {/* Header */}
      <PublicNavbar />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 mt-16">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4" variant="outline">About {siteName}</Badge>
          <h1 className="text-5xl font-bold mb-6">
            {renderHTML(getContent('hero-title', 'Professional Database Management')) || (
              <>Professional Database Management <span className="text-primary">Made Simple</span></>
            )}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            {renderHTML(getContent('hero-subtitle', seoDescription)) || seoDescription}
          </p>
        </div>
      </section>

      {/* Key Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Comprehensive Feature Set</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Everything you need to manage, query, and analyze your databases efficiently
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index} 
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-primary/10 ${feature.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Supported Databases */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Supported Databases</h2>
            <p className="text-muted-foreground">
              Connect to 12+ popular database systems
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {databaseTypes.map((db, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-lg p-4 flex items-center gap-3 hover:border-primary/50 transition-colors"
              >
                <span className="text-2xl">{db.icon}</span>
                <span className="font-medium">{db.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Choose {siteName}?</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">No Installation Required</h4>
                  <p className="text-sm text-muted-foreground">Access from any browser, any device. No software to download or maintain.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Instant Access</h4>
                  <p className="text-sm text-muted-foreground">Start managing databases in seconds. No complex setup or configuration.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">AI-Powered Intelligence</h4>
                  <p className="text-sm text-muted-foreground">Let AI help you write queries, understand data, and optimize performance.</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Pay-Per-Use Model</h4>
                  <p className="text-sm text-muted-foreground">No subscriptions. Buy credits as you need them. Credits never expire.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Enterprise Security</h4>
                  <p className="text-sm text-muted-foreground">Bank-level encryption, secure connections, and compliance with data protection regulations.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-1">Regular Updates</h4>
                  <p className="text-sm text-muted-foreground">Continuous improvements, new features, and database support added regularly.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-12 text-center border border-primary/20">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-8 text-lg">
            Join thousands of developers and database administrators who trust {siteName}
          </p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/register')} className="gap-2">
              Start Free Trial
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/')}>
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-5 h-5 object-contain" />
            ) : (
              <Database className="w-5 h-5 text-primary" />
            )}
            <span className="font-semibold">{siteName}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button onClick={() => navigate('/about')} className="text-sm text-muted-foreground hover:text-foreground">
              About
            </button>
            <button onClick={() => navigate('/privacy')} className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </button>
            <button onClick={() => navigate('/#support')} className="text-sm text-muted-foreground hover:text-foreground">
              FAQ
            </button>
            <a 
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Admin Login
            </a>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
